/**
 * Item Model - Production-Grade Menu Item
 *
 * Universal item model designed for sync with all platforms:
 * - Uber Eats, DoorDash, Grubhub
 * - Google Business Profile
 * - Square, Toast, Clover
 * - POS, Kiosk, Online Ordering
 *
 * All new fields are optional to maintain backwards compatibility.
 */

import { ComponentGroup, IComponentGroup } from "./ComponentGroup";
import { ItemVariant } from "./ItemVariant";
import { MenuGroupReference } from "./MenuGroupReference";
import { ModifierGroupReference } from "./ModifierGroupReference";
import { SizePrice } from "./SizePrice";
import { isInStock } from "./stockAvailability";
import type { IBarWineItemTag } from "../bar/BarWineList";
import type { IItemIngredientStock } from "./ingredientAvailability";
import type { SystemItemType } from "./SystemItem";
import type {
  AccountingInfo,
  // Regulatory
  AgeRestriction,
  AlcoholInfo,
  Allergen,
  AvailabilityWindow,
  BundledItem,
  ChannelPriceOverride,
  CostInfo,
  CuisineType,
  // Dietary & Allergens
  DietaryLabel,
  DiscoveryInfo,
  ExternalPlatformId,
  // Classification
  ItemType,
  MediaAsset,
  // Composite types
  NutritionalInfo,
  PhysicalProperties,
  PortionSize,
  PreparationInfo,
  PreparationMethod,
  PreparationType,
  // Pricing
  PricingStrategy,
  ProductCategory,
  // Availability
  SalesChannel,
  SeasonalAvailability,
  SpicyLevel,
  StockInfo,
  TasteProfile,
  TaxClassification,
  TemperatureCategory,
  Translation,
} from "./types";

// ============================================================================
// INTERFACES
// ============================================================================

/** One `IItem.menuGroupModifierPrices` entry: this item's price for an option on one menu. */
export interface IItemMenuModifierPrice {
  readonly menuGroupId: number;
  readonly modifierGroupId: number;
  readonly modifierId: number;
  readonly price: number;
}

export interface IItem {
  // ─────────────────────────────────────────────────────────────────────────
  // IDENTITY
  // ─────────────────────────────────────────────────────────────────────────
  /** Internal auto-increment ID */
  readonly itemId?: number;
  /** Globally unique identifier (UUID) */
  readonly guid?: string;
  /** Human-readable item number/code */
  readonly number?: number;
  /** Customer-facing name */
  readonly name: string;

  // ─────────────────────────────────────────────────────────────────────────
  // PRODUCT CODES (for scanning, inventory, platforms)
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Price Look-Up code (4-5 digit, produce).
   * `null` on an update payload means CLEAR (see {@link Item} class note).
   */
  readonly plu?: string | null;
  /**
   * Stock Keeping Unit (internal inventory).
   * `null` on an update payload means CLEAR.
   */
  readonly sku?: string | null;
  /** Universal Product Code (12-digit barcode) */
  readonly upc?: string;
  /** European Article Number (13-digit barcode) */
  readonly ean?: string;
  /** Global Trade Item Number (standardized product ID) */
  readonly gtin?: string;
  /** Alternate barcode for scanning */
  readonly barcode?: string;

  // ─────────────────────────────────────────────────────────────────────────
  // DESCRIPTIONS (for different contexts)
  // ─────────────────────────────────────────────────────────────────────────
  /** Short name for POS buttons. `null` on an update payload means CLEAR. */
  readonly posName?: string | null;
  /** Internal description for staff. `null` on an update payload means CLEAR. */
  readonly posDescription?: string | null;
  /** Full description for customers/online. `null` on an update payload means CLEAR. */
  readonly customerDescription?: string | null;
  /** Short tagline for menus (e.g., "Fan Favorite!") */
  readonly tagline?: string;
  /** Kitchen ticket name. `null` on an update payload means CLEAR. */
  readonly kitchenName?: string | null;
  /** Receipt/invoice name */
  readonly receiptName?: string;

  // ─────────────────────────────────────────────────────────────────────────
  // TRANSLATIONS (multi-language support)
  // ─────────────────────────────────────────────────────────────────────────
  /** Translations for international markets */
  readonly translations?: Translation[];

  // ─────────────────────────────────────────────────────────────────────────
  // MEDIA / IMAGES
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Primary image URL, or a JSON-encoded `OptimizedImage` (backwards
   * compatible — see `resolveImageUrl` in `@order-up/common`).
   * `null` on an update payload means CLEAR (see {@link Item} class note).
   */
  readonly imageUrl?: string | null;
  /** All media assets (images, videos) */
  readonly media?: MediaAsset[];
  /** Thumbnail image URL (for lists) */
  readonly thumbnailUrl?: string;

  // ─────────────────────────────────────────────────────────────────────────
  // DISPLAY / UI
  // ─────────────────────────────────────────────────────────────────────────
  /** POS button color (hex code). `null` on an update payload means CLEAR. */
  readonly posColor?: string | null;
  /** KDS display color (hex code). `null` on an update payload means CLEAR. */
  readonly kdsColor?: string | null;
  /** Sort/display position in category */
  readonly position?: number;

  // ─────────────────────────────────────────────────────────────────────────
  // PRICING
  // ─────────────────────────────────────────────────────────────────────────
  /** Pricing strategy: base, size, open, variant */
  readonly pricingStrategy: PricingStrategy;
  /** Base price in currency units (e.g. dollars) */
  readonly basePrice?: number;
  /** Core/intrinsic price for refunds (Uber Eats), in currency units */
  readonly corePrice?: number;
  /** Prices by size (Small, Medium, Large) */
  readonly sizePrice: SizePrice[];
  /** Allow operator to enter custom price */
  readonly openPrice: boolean;

  /**
   * Marks this as a SYSTEM item (Open Food / Open Drink) rather than a menu
   * item the restaurant sells by name.
   *
   * Absent on every ordinary item, and absence is the safe default: nothing
   * treats an undefined value as a system item. See `SystemItem.ts` for the
   * three invariants — not archivable, not assignable to a menu group, not
   * renameable — and for why "not assignable" is what keeps these off every
   * marketplace with no exclusion list to maintain.
   */
  readonly systemItemType?: SystemItemType | null;
  /** Variant combinations with prices */
  readonly variants: ItemVariant[];
  /** Dimension names for variants: ["Flavor", "Size"] */
  readonly variantOptions?: string[];
  /** Channel-specific price overrides */
  readonly channelPrices?: ChannelPriceOverride[];
  /** Delivery markup percentage */
  readonly deliveryMarkupPercent?: number;
  /** Minimum price (for open price items), in currency units */
  readonly minPrice?: number;
  /** Maximum price (for open price items), in currency units */
  readonly maxPrice?: number;
  /**
   * Menu group price references for menu-specific pricing.
   * Used when pricingStrategy is "menu_specific".
   * Each reference maps a menu group to a specific price for this item.
   * basePrice serves as the default fallback for groups without a reference.
   */
  readonly menuGroupPrices: MenuGroupReference[];

  // ─────────────────────────────────────────────────────────────────────────
  // TAXES
  // ─────────────────────────────────────────────────────────────────────────
  /** Tax rate IDs to apply */
  readonly taxRateIds: number[];
  /** Is price tax-inclusive? */
  readonly taxInclusion: boolean;
  /** Different tax treatment for takeout */
  readonly takeoutTaxException: boolean;
  /** Tax classification labels for platforms */
  readonly taxClassifications?: TaxClassification[];
  /** VAT rate for EU markets (percentage) */
  readonly vatRatePercent?: number;

  // ─────────────────────────────────────────────────────────────────────────
  // DISCOUNTS
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Discounts and promo codes never come off this item; comps still do.
   *
   * For items a restaurant will not sell below price (gift cards, alcohol in
   * a state that forbids drink discounts, a fixed-price special). A comp is a
   * write-off for something that went wrong, not a price, so it still reaches
   * the line. Absent reads as discountable, which is every item stored before
   * this field existed.
   */
  readonly excludeFromDiscounts?: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // MODIFIERS
  // ─────────────────────────────────────────────────────────────────────────
  /** Associated modifier groups */
  readonly modifierGroups: ModifierGroupReference[];

  // ─────────────────────────────────────────────────────────────────────────
  // BUNDLED ITEMS (for combos/meal deals)
  // ─────────────────────────────────────────────────────────────────────────
  /** Items always included (combo components) */
  readonly bundledItems?: BundledItem[];
  /** Is this a combo/meal deal? */
  readonly isCombo?: boolean;
  /** Core price for combos (value before discounts), in currency units */
  readonly comboCorePrice?: number;

  // ─────────────────────────────────────────────────────────────────────────
  // COMPONENT GROUPS (composite items — "choose N of these ITEMS")
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * "Choose N" prompts whose options are REAL items. Present (and non-empty)
   * makes this a composite: at ring-up it expands into a parent line carrying
   * the price plus child lines that are ordinary order items, so each pick
   * keeps its own course, prep stations, sales category and recipe.
   *
   * Distinct from `bundledItems`, which is FIXED inclusion used for upsell
   * recommendations and the Uber Eats projection — it is not customer choice.
   *
   * See ComponentGroup.ts and compositeItemResolver.ts.
   */
  readonly componentGroups?: IComponentGroup[];

  // ─────────────────────────────────────────────────────────────────────────
  // KITCHEN / PREPARATION
  // ─────────────────────────────────────────────────────────────────────────
  /** Prep stations that handle this item */
  readonly prepStationIds: number[];
  /** Course assignment (Appetizer, Entree, etc.). `null` on an update payload means CLEAR. */
  readonly courseId?: number | null;
  /** Prep and cooking time info */
  readonly preparationInfo?: PreparationInfo;
  /** Prep time in minutes (convenience field) */
  readonly prepTimeMinutes?: number;
  /** Cook time in minutes (convenience field) */
  readonly cookTimeMinutes?: number;

  // ─────────────────────────────────────────────────────────────────────────
  // NUTRITIONAL INFORMATION
  // ─────────────────────────────────────────────────────────────────────────
  /** Calories per serving (backwards compatible). `null` on an update payload means CLEAR. */
  readonly calories?: number | null;
  /** Full nutritional info (FDA label data) */
  readonly nutritionalInfo?: NutritionalInfo;
  /** Dietary labels (Vegan, GF, etc.) */
  readonly dietaryLabels?: DietaryLabel[];
  /** Allergen warnings */
  readonly allergens?: Allergen[];
  /** Ingredients list */
  readonly ingredients?: string[];
  /** Additives list */
  readonly additives?: string[];
  /** Number of people served (Google FoodMenus) */
  readonly servesNumPeople?: number;
  /** Portion size (Google FoodMenus) */
  readonly portionSize?: PortionSize;
  /** Preparation methods (Google FoodMenus) */
  readonly preparationMethods?: PreparationMethod[];

  // ─────────────────────────────────────────────────────────────────────────
  // TASTE / DISCOVERY
  // ─────────────────────────────────────────────────────────────────────────
  /** Spicy heat level */
  readonly spicyLevel?: SpicyLevel;
  /** Taste profiles for recommendations */
  readonly tasteProfiles?: TasteProfile[];
  /** Discovery/SEO metadata */
  readonly discoveryInfo?: DiscoveryInfo;

  // ─────────────────────────────────────────────────────────────────────────
  // CLASSIFICATION
  // ─────────────────────────────────────────────────────────────────────────
  /** High-level item type (Food, Beverage, Alcohol) */
  readonly itemType?: ItemType;
  /** Sales category for reporting. `null` on an update payload means CLEAR. */
  readonly salesCategoryId?: number | null;
  // Note: menuGroupIds removed - menu group relationships are owned by MenuGroup.items[]
  // To find which groups an item belongs to, query MenuGroup.items.some(ref => ref.itemId === itemId)
  /** Product category (Entree, Beverage, etc.) */
  readonly productCategory?: ProductCategory;
  /** Cuisine type (Mexican, Italian, etc.) */
  readonly cuisineType?: CuisineType;
  /** Preparation type (Made to order, Prepackaged) */
  readonly preparationType?: PreparationType;
  /** Tags for filtering */
  readonly tagIds: number[];
  /** Allergy category IDs from platform allergy_categories collection */
  readonly allergyCategoryIds: number[];
  /** Ingredient IDs from platform ingredients collection */
  readonly ingredientIds: number[];
  /**
   * TENANT-SCOPED allergen/ingredient link to this restaurant's RecipeOps
   * `inventory_items` (by their stable GUID). Distinct from `ingredientIds`
   * (the global, admin-owned, cross-tenant platform `ingredients`): this link
   * lives on the tenant's own menu item so it never collides across tenants
   * (Decision 4 — RecipeOps rework). RecipeOps-only; empty/undefined otherwise.
   */
  readonly linkedInventoryGuids?: string[];
  /**
   * Set when this drink was adopted from the OrderUp bar catalog
   * (`bar_catalog_items/{guid}`). Its build card (`posDescription`), its
   * `ingredientIds` and its recipe are then OWNED by the catalog: every publish
   * rewrites them, the item editor shows them read-only, and the client item
   * service refuses a save that changes them (`bar/barLinkedFields.ts`). Written
   * by the API only; absent on every item a restaurant built itself.
   */
  readonly barCatalogEntryGuid?: string | null;
  /**
   * On a generated mixed-drink item (`items/bar_mixed_{spirit}`): the photo
   * OrderUp last wrote as `imageUrl` from the house bottle (design 2026-10-05
   * Part 1, `barMixedItemImagePatch`). While `imageUrl` equals it the photo
   * is OrderUp's and follows the house bottle; any other photo is the
   * restaurant's and is never overwritten. Written by the API only.
   */
  readonly barImageSource?: string | null;
  /**
   * On a listed WINE's item (`items/bar_wine_{inventoryGuid}`): the bottle its
   * servings pour from — the shared "Wine Serving" options name no wine (design
   * 2026-10-16), so a till recognises a glass, and the API composes its
   * open-bottle note, from this (`barWineServingTag`). Written by the API only.
   */
  readonly barWine?: IBarWineItemTag | null;
  /**
   * This item's own price for an option of one of its modifier groups ON ONE
   * MENU — a wine's Happy Hour Glass, say. Read by `resolveModifierPriceOverrides`
   * ahead of every other step: the first menu on the ROOT→LEAF path that
   * carries entries for the group answers with all of them, over the menu's
   * price for every item and the item's own all-menus price. The wine list's
   * shared Glass and Bottle are priced per wine and per menu here, so no
   * option is ever copied per wine (owner, 2026-10-16).
   */
  readonly menuGroupModifierPrices?: ReadonlyArray<IItemMenuModifierPrice> | null;
  /**
   * Ingredient availability — SERVER-WRITE-ONLY, written by
   * `IngredientAvailabilityService` from this item's recipe and the
   * restaurant's `inventory_items`. `blocked` = a vital ingredient is out (read
   * by `isInStock`); `notice` is appended to guest-facing descriptions by
   * `customerFacingDescription`. Absent/null = no ingredient effect.
   * docs/superpowers/specs/2026-10-01-ingredient-availability-design.md
   */
  readonly ingredientStock?: IItemIngredientStock | null;

  // ─────────────────────────────────────────────────────────────────────────
  // REGULATORY / COMPLIANCE
  // ─────────────────────────────────────────────────────────────────────────
  /** Contains alcohol (backwards compatible) */
  readonly containsAlcohol: boolean;
  /** Detailed alcohol information */
  readonly alcoholInfo?: AlcoholInfo;
  /** Age restriction requirement */
  readonly ageRestriction?: AgeRestriction;
  /** Contains tobacco/nicotine */
  readonly containsTobacco?: boolean;
  /** Contains cannabis */
  readonly containsCannabis?: boolean;
  /** Prop 65 warning (California) */
  readonly prop65Warning?: string;
  /** Country of origin */
  readonly countryOfOrigin?: string;

  // ─────────────────────────────────────────────────────────────────────────
  // PHYSICAL PROPERTIES (for delivery/shipping)
  // ─────────────────────────────────────────────────────────────────────────
  /** Physical properties (weight, dimensions) */
  readonly physicalProperties?: PhysicalProperties;
  /** Temperature category for delivery */
  readonly temperatureCategory?: TemperatureCategory;
  /** Reusable packaging flag */
  readonly hasReusablePackaging?: boolean;
  /** Container deposit amount (currency units) */
  readonly containerDeposit?: number;

  // ─────────────────────────────────────────────────────────────────────────
  // CHANNEL VISIBILITY
  // ─────────────────────────────────────────────────────────────────────────
  /** Available on POS */
  readonly pos: boolean;
  /** Available on kiosk */
  readonly kiosk: boolean;
  /** Available for online ordering */
  readonly onlineOrdering: boolean;
  /** Available on customer invoices */
  readonly customerInvoices: boolean;
  /** Available to third-party partners */
  readonly onlineOrderingPartners: boolean;
  /** Enabled sales channels (granular control) */
  readonly enabledChannels?: SalesChannel[];
  /** Disabled sales channels (granular control) */
  readonly disabledChannels?: SalesChannel[];

  // ─────────────────────────────────────────────────────────────────────────
  // AVAILABILITY / STOCK
  // ─────────────────────────────────────────────────────────────────────────
  /** Stock/availability info */
  readonly stockInfo?: StockInfo;
  /** Is item currently suspended (86'd)? */
  readonly isSuspended?: boolean;
  /** Suspension end time */
  readonly suspendedUntil?: Date;

  // ─────────────────────────────────────────────────────────────────────────
  // AVAILABILITY / QUANTITY TRACKING
  // ─────────────────────────────────────────────────────────────────────────
  /** Always available? If true, quantity tracking is disabled. */
  readonly alwaysAvailable?: boolean;
  /**
   * Remaining quantity available for sale. Only used when alwaysAvailable is
   * false. Must be >= 0. `null` on an update payload means CLEAR — switching
   * back to "always available" MUST drop the stale count.
   */
  readonly quantityRemaining?: number | null;

  /** Time windows when available */
  readonly availabilityWindows?: AvailabilityWindow[];
  /** Seasonal availability */
  readonly seasonalAvailability?: SeasonalAvailability;

  // ─────────────────────────────────────────────────────────────────────────
  // COST / ACCOUNTING
  // ─────────────────────────────────────────────────────────────────────────
  /** Cost and margin info */
  readonly costInfo?: CostInfo;
  /** Accounting codes */
  readonly accountingInfo?: AccountingInfo;
  /**
   * RecipeOps link (Phase 14-R): the operational recipe (`recipe_items.guid`)
   * for this item. Convenience/display pointer set when a recipe is linked —
   * NON-authoritative. The order-close decrement resolver matches recipes by
   * `recipe_items.menuItemGuid === Item.guid`, never by this field.
   */
  readonly recipeGuid?: string | null;

  // ─────────────────────────────────────────────────────────────────────────
  // PLATFORM SYNC
  // ─────────────────────────────────────────────────────────────────────────
  /** External IDs on various platforms */
  readonly externalIds?: ExternalPlatformId[];
  /** External data blob (for platform-specific data) */
  readonly externalData?: string;
  /** Last sync timestamp */
  readonly lastSyncedAt?: Date;

  // ─────────────────────────────────────────────────────────────────────────
  // METADATA
  // ─────────────────────────────────────────────────────────────────────────
  /** Ordering rules (reserved) */
  readonly orderingRules?: never;
  /** Created timestamp */
  readonly createdAt?: Date;
  /** Updated timestamp */
  readonly updatedAt?: Date;
  /** Created by user ID */
  readonly createdBy?: string;
  /** Updated by user ID */
  readonly updatedBy?: string;
  /** Is item active/enabled? */
  readonly isActive?: boolean;
  /** Is item archived/hidden? */
  readonly isArchived?: boolean;
}

export type IItemData = Partial<IItem>;

// ============================================================================
// CLASS IMPLEMENTATION
// ============================================================================

/**
 * NULL SEMANTICS
 * ---------------------------------------------------------------------------
 * Several optional {@link IItem} fields accept `null` so an EDIT can express
 * "clear this field". `null` is a WIRE-LEVEL signal only: the client service
 * translates it into a Firestore `deleteField()` tombstone, so a hydrated item
 * never holds `null`. The class therefore normalizes `null` → `undefined` in
 * the constructor and keeps its own field types non-nullable — read sites stay
 * `string | undefined` and need no null handling.
 */
export class Item implements IItem {
  // Identity
  readonly itemId?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly name: string;

  // Product codes
  readonly plu?: string;
  readonly sku?: string;
  readonly upc?: string;
  readonly ean?: string;
  readonly gtin?: string;
  readonly barcode?: string;

  // Descriptions
  readonly posName?: string;
  readonly posDescription?: string;
  readonly customerDescription?: string;
  readonly tagline?: string;
  readonly kitchenName?: string;
  readonly receiptName?: string;

  // Translations
  readonly translations?: Translation[];

  // Media
  readonly imageUrl?: string;
  readonly media?: MediaAsset[];
  readonly thumbnailUrl?: string;

  // Display
  readonly posColor?: string;
  readonly kdsColor?: string;
  readonly position?: number;

  // Pricing
  readonly pricingStrategy: PricingStrategy;
  readonly basePrice?: number;
  readonly corePrice?: number;
  readonly sizePrice: SizePrice[];
  readonly openPrice: boolean;
  readonly systemItemType?: SystemItemType;
  readonly variants: ItemVariant[];
  readonly variantOptions?: string[];
  readonly channelPrices?: ChannelPriceOverride[];
  readonly deliveryMarkupPercent?: number;
  readonly minPrice?: number;
  readonly maxPrice?: number;
  readonly menuGroupPrices: MenuGroupReference[];

  // Taxes
  readonly taxRateIds: number[];
  readonly taxInclusion: boolean;
  readonly takeoutTaxException: boolean;
  readonly taxClassifications?: TaxClassification[];
  readonly vatRatePercent?: number;

  // Discounts
  readonly excludeFromDiscounts: boolean;

  // Modifiers
  readonly modifierGroups: ModifierGroupReference[];

  // Bundled items
  readonly bundledItems?: BundledItem[];
  readonly isCombo?: boolean;
  readonly comboCorePrice?: number;

  // Component groups (composite items)
  readonly componentGroups?: ComponentGroup[];
  // Kitchen
  readonly prepStationIds: number[];
  readonly courseId?: number;
  readonly preparationInfo?: PreparationInfo;
  readonly prepTimeMinutes?: number;
  readonly cookTimeMinutes?: number;

  // Nutritional
  readonly calories?: number;
  readonly nutritionalInfo?: NutritionalInfo;
  readonly dietaryLabels?: DietaryLabel[];
  readonly allergens?: Allergen[];
  readonly ingredients?: string[];
  readonly additives?: string[];
  readonly servesNumPeople?: number;
  readonly portionSize?: PortionSize;
  readonly preparationMethods?: PreparationMethod[];

  // Taste/Discovery
  readonly spicyLevel?: SpicyLevel;
  readonly tasteProfiles?: TasteProfile[];
  readonly discoveryInfo?: DiscoveryInfo;

  // Classification
  readonly itemType?: ItemType;
  readonly salesCategoryId?: number;
  // Note: menuGroupIds removed - menu group relationships are owned by MenuGroup.items[]
  // Menu group pricing is handled via menuGroupPrices[] when pricingStrategy is "menu_specific"
  readonly productCategory?: ProductCategory;
  readonly cuisineType?: CuisineType;
  readonly preparationType?: PreparationType;
  readonly tagIds: number[];
  readonly allergyCategoryIds: number[];
  readonly ingredientIds: number[];
  readonly linkedInventoryGuids?: string[];
  readonly barCatalogEntryGuid?: string;
  readonly barImageSource?: string;
  readonly barWine?: IBarWineItemTag;
  readonly menuGroupModifierPrices?: ReadonlyArray<IItemMenuModifierPrice>;
  readonly ingredientStock?: IItemIngredientStock | null;

  // Regulatory
  readonly containsAlcohol: boolean;
  readonly alcoholInfo?: AlcoholInfo;
  readonly ageRestriction?: AgeRestriction;
  readonly containsTobacco?: boolean;
  readonly containsCannabis?: boolean;
  readonly prop65Warning?: string;
  readonly countryOfOrigin?: string;

  // Physical
  readonly physicalProperties?: PhysicalProperties;
  readonly temperatureCategory?: TemperatureCategory;
  readonly hasReusablePackaging?: boolean;
  readonly containerDeposit?: number;

  // Channel visibility
  readonly pos: boolean;
  readonly kiosk: boolean;
  readonly onlineOrdering: boolean;
  readonly customerInvoices: boolean;
  readonly onlineOrderingPartners: boolean;
  readonly enabledChannels?: SalesChannel[];
  readonly disabledChannels?: SalesChannel[];

  // Availability
  readonly stockInfo?: StockInfo;
  readonly isSuspended?: boolean;
  readonly suspendedUntil?: Date;
  readonly alwaysAvailable?: boolean;
  readonly quantityRemaining?: number;
  readonly availabilityWindows?: AvailabilityWindow[];
  readonly seasonalAvailability?: SeasonalAvailability;

  // Cost/Accounting
  readonly costInfo?: CostInfo;
  readonly accountingInfo?: AccountingInfo;
  readonly recipeGuid?: string | null;

  // Platform sync
  readonly externalIds?: ExternalPlatformId[];
  readonly externalData?: string;
  readonly lastSyncedAt?: Date;

  // Metadata
  readonly orderingRules?: never;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly createdBy?: string;
  readonly updatedBy?: string;
  readonly isActive?: boolean;
  readonly isArchived?: boolean;

  constructor(data: IItemData) {
    // Identity
    this.itemId = data.itemId;
    this.guid = data.guid;
    this.number = data.number;
    this.name = data.name!;

    // Product codes.
    // `?? undefined` collapses the wire-level "clear this field" null (see the
    // NULL SEMANTICS note above) so instances only ever hold string|undefined.
    this.plu = data.plu ?? undefined;
    this.sku = data.sku ?? undefined;
    this.upc = data.upc;
    this.ean = data.ean;
    this.gtin = data.gtin;
    this.barcode = data.barcode;

    // Descriptions
    this.posName = data.posName ?? undefined;
    this.posDescription = data.posDescription ?? undefined;
    this.customerDescription = data.customerDescription ?? undefined;
    this.tagline = data.tagline;
    this.kitchenName = data.kitchenName ?? undefined;
    this.receiptName = data.receiptName;

    // Translations
    this.translations = data.translations;

    // Media
    this.imageUrl = data.imageUrl ?? undefined;
    this.media = data.media;
    this.thumbnailUrl = data.thumbnailUrl;

    // Display
    this.posColor = data.posColor ?? undefined;
    this.kdsColor = data.kdsColor ?? undefined;
    this.position = data.position;

    // Pricing
    this.pricingStrategy = data.pricingStrategy ?? "base";
    this.basePrice = data.basePrice;
    this.corePrice = data.corePrice;
    this.sizePrice = (data.sizePrice ?? []).map((sp) => new SizePrice(sp));
    this.openPrice = data.openPrice ?? false;
    this.systemItemType = data.systemItemType ?? undefined;
    this.variants = (data.variants ?? []).map((v) => new ItemVariant(v));
    this.variantOptions = data.variantOptions;
    this.channelPrices = data.channelPrices;
    this.deliveryMarkupPercent = data.deliveryMarkupPercent;
    this.minPrice = data.minPrice;
    this.maxPrice = data.maxPrice;
    this.menuGroupPrices = (data.menuGroupPrices ?? []).map(
      (p) => new MenuGroupReference(p),
    );

    // Taxes
    this.taxRateIds = data.taxRateIds ?? [];
    this.taxInclusion = data.taxInclusion ?? false;
    this.takeoutTaxException = data.takeoutTaxException ?? false;
    this.taxClassifications = data.taxClassifications;
    this.vatRatePercent = data.vatRatePercent;

    // Discounts
    this.excludeFromDiscounts = data.excludeFromDiscounts ?? false;

    // Modifiers
    this.modifierGroups = (data.modifierGroups ?? []).map(
      (mg) => new ModifierGroupReference(mg),
    );

    // Bundled items
    this.bundledItems = data.bundledItems;
    this.isCombo = data.isCombo;
    this.comboCorePrice = data.comboCorePrice;

    // Component groups (composite items)
    this.componentGroups = data.componentGroups
      ? data.componentGroups.map((cg) => new ComponentGroup(cg))
      : undefined;

    // Kitchen
    this.prepStationIds = data.prepStationIds ?? [];
    this.courseId = data.courseId ?? undefined;
    this.preparationInfo = data.preparationInfo;
    this.prepTimeMinutes = data.prepTimeMinutes;
    this.cookTimeMinutes = data.cookTimeMinutes;

    // Nutritional
    this.calories = data.calories ?? undefined;
    this.nutritionalInfo = data.nutritionalInfo;
    this.dietaryLabels = data.dietaryLabels;
    this.allergens = data.allergens;
    this.ingredients = data.ingredients;
    this.additives = data.additives;
    this.servesNumPeople = data.servesNumPeople;
    this.portionSize = data.portionSize;
    this.preparationMethods = data.preparationMethods;

    // Taste/Discovery
    this.spicyLevel = data.spicyLevel;
    this.tasteProfiles = data.tasteProfiles;
    this.discoveryInfo = data.discoveryInfo;

    // Classification
    this.itemType = data.itemType;
    this.salesCategoryId = data.salesCategoryId ?? undefined;
    // menuGroupIds removed - owned by MenuGroup.items[]
    this.productCategory = data.productCategory;
    this.cuisineType = data.cuisineType;
    this.preparationType = data.preparationType;
    this.tagIds = data.tagIds ?? [];
    this.allergyCategoryIds = data.allergyCategoryIds ?? [];
    this.ingredientIds = data.ingredientIds ?? [];
    this.linkedInventoryGuids = data.linkedInventoryGuids;
    this.barCatalogEntryGuid = data.barCatalogEntryGuid ?? undefined;
    this.barImageSource = data.barImageSource ?? undefined;
    this.barWine = data.barWine ?? undefined;
    this.menuGroupModifierPrices = data.menuGroupModifierPrices ?? undefined;
    this.ingredientStock = data.ingredientStock ?? undefined;

    // Regulatory
    this.containsAlcohol = data.containsAlcohol ?? false;
    this.alcoholInfo = data.alcoholInfo;
    this.ageRestriction = data.ageRestriction;
    this.containsTobacco = data.containsTobacco;
    this.containsCannabis = data.containsCannabis;
    this.prop65Warning = data.prop65Warning;
    this.countryOfOrigin = data.countryOfOrigin;

    // Physical
    this.physicalProperties = data.physicalProperties;
    this.temperatureCategory = data.temperatureCategory;
    this.hasReusablePackaging = data.hasReusablePackaging;
    this.containerDeposit = data.containerDeposit;

    // Channel visibility
    this.pos = data.pos ?? true;
    this.kiosk = data.kiosk ?? true;
    this.onlineOrdering = data.onlineOrdering ?? true;
    this.customerInvoices = data.customerInvoices ?? true;
    this.onlineOrderingPartners = data.onlineOrderingPartners ?? true;
    this.enabledChannels = data.enabledChannels;
    this.disabledChannels = data.disabledChannels;

    // Availability
    this.stockInfo = data.stockInfo;
    this.isSuspended = data.isSuspended;
    this.suspendedUntil = data.suspendedUntil;
    this.alwaysAvailable = data.alwaysAvailable;
    this.quantityRemaining =
      data.quantityRemaining !== undefined && data.quantityRemaining !== null
        ? Math.max(0, Math.floor(data.quantityRemaining))
        : undefined;
    this.availabilityWindows = data.availabilityWindows;
    this.seasonalAvailability = data.seasonalAvailability;

    // Cost/Accounting
    this.costInfo = data.costInfo;
    this.accountingInfo = data.accountingInfo;
    this.recipeGuid = data.recipeGuid;

    // Platform sync
    this.externalIds = data.externalIds;
    this.externalData = data.externalData;
    this.lastSyncedAt = data.lastSyncedAt;

    // Metadata
    this.orderingRules = data.orderingRules;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
    this.createdBy = data.createdBy;
    this.updatedBy = data.updatedBy;
    this.isActive = data.isActive ?? true;
    this.isArchived = data.isArchived ?? false;
  }

  // ============================================================================
  // SERIALIZATION
  // ============================================================================

  toJSON(): IItem {
    return {
      ...this,
      sizePrice: this.sizePrice.map((sp) => sp.toJSON()),
      variants: this.variants.map((v) => v.toJSON()),
      menuGroupPrices: this.menuGroupPrices.map((p) => p.toJSON()),
      modifierGroups: this.modifierGroups.map((mg) => mg.toJSON()),
      // Composite prompts. Without this the spread above emits ComponentGroup
      // CLASS INSTANCES into what Firestore is asked to persist.
      ...(this.componentGroups
        ? { componentGroups: this.componentGroups.map((cg) => cg.toJSON()) }
        : {}),
    };
  }

  static fromJSON(raw: Record<string, unknown>): Item {
    if (!raw.name) throw new Error("Item.fromJSON: name is missing.");
    // 🔴 NESTED DATES ARE DATES TOO. Firestore reads every stored Date back as
    // a Timestamp, and this converted only the four top-level date fields below.
    // A Google menu publish stamps `externalIds[].lastSynced`, so on 2026-09-11
    // House of Pho's 38 Google-synced items came back with a raw Timestamp
    // there — and `updateItem` validates the MERGED document against a schema
    // that says `z.date()`, so every save of those items failed on a field the
    // operator never touched. `stockInfo` and `costInfo` carry dates the same
    // way. Converting them HERE, where every item is hydrated, is what keeps
    // the model's `Date` type honest; the schema is not widened to accept a
    // Firestore type. Pinned by packages/client/test/services/itemNestedTimestamps.test.ts,
    // which walks the schema for every date it declares.
    const data = Item._hydrateNestedTimestamps(raw) as Record<string, unknown>;

    // Import centralized timestamp converter at runtime
    // Note: We can't import at the top due to circular dependencies between packages
    // The converter is available via @order-up/common/utils/timestamp
    const convertTimestamp = (timestamp: unknown): Date | undefined => {
      if (!timestamp) return undefined;
      // Check if it's a Firestore Timestamp object
      if (
        timestamp &&
        typeof timestamp === "object" &&
        "toDate" in timestamp &&
        typeof (timestamp as { toDate: () => Date }).toDate === "function"
      ) {
        return (timestamp as { toDate: () => Date }).toDate();
      }
      // Check if it's already a Date object
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Try to parse as string/number
      try {
        const date = new Date(timestamp as string | number);
        return isNaN(date.getTime()) ? undefined : date;
      } catch {
        return undefined;
      }
    };

    return new Item({
      ...data,
      itemType:
        (data.itemType as ItemType) ||
        (data.containsAlcohol ? "ALCOHOLIC_BEVERAGE" : "FOOD"),
      name: data.name as string,
      sizePrice: Array.isArray(data.sizePrice)
        ? data.sizePrice.map((sp) => SizePrice.fromJSON(sp))
        : [],
      variants: Array.isArray(data.variants)
        ? data.variants.map((v) => ItemVariant.fromJSON(v))
        : [],
      menuGroupPrices: Array.isArray(data.menuGroupPrices)
        ? data.menuGroupPrices.map((p) => MenuGroupReference.fromJSON(p))
        : Item._migrateMenuSpecificPrices(data),
      modifierGroups: Array.isArray(data.modifierGroups)
        ? data.modifierGroups.map((mg) => ModifierGroupReference.fromJSON(mg))
        : [],
      // Absent stays ABSENT: `componentGroups: []` is not a composite, and
      // defaulting to [] here would stamp the key onto every ordinary item.
      ...(Array.isArray(data.componentGroups)
        ? {
            componentGroups: data.componentGroups.map((cg) =>
              ComponentGroup.fromJSON(cg as Record<string, unknown>),
            ),
          }
        : {}),
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      lastSyncedAt: convertTimestamp(data.lastSyncedAt),
      suspendedUntil: convertTimestamp(data.suspendedUntil),
    } as IItemData);
  }

  /**
   * Replace every Firestore Timestamp (anything with a `toDate()` method) found
   * inside plain objects and arrays with the Date it denotes.
   *
   * Class instances are left alone: they hydrate themselves, and walking into
   * one would rebuild it as a plain object. Dates, primitives and nulls pass
   * through unchanged, so running this on already-hydrated data is a no-op.
   */
  private static _hydrateNestedTimestamps(value: unknown): unknown {
    if (value === null || typeof value !== "object" || value instanceof Date) {
      return value;
    }
    if (typeof (value as { toDate?: unknown }).toDate === "function") {
      try {
        const date = (value as { toDate: () => Date }).toDate();
        return date instanceof Date && !isNaN(date.getTime()) ? date : value;
      } catch {
        return value;
      }
    }
    if (Array.isArray(value)) {
      return value.map((element) => Item._hydrateNestedTimestamps(element));
    }
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      out[key] = Item._hydrateNestedTimestamps(child);
    }
    return out;
  }

  // ============================================================================
  // MIGRATION HELPERS
  // ============================================================================

  /**
   * Migrates legacy menuSpecificPrices (Record<string, number>) to MenuGroupReference[].
   * This handles backward compatibility with old Firestore documents that used
   * the flat map format before the MenuGroupReference architecture was implemented.
   */
  private static _migrateMenuSpecificPrices(
    data: Record<string, unknown>,
  ): MenuGroupReference[] {
    const legacy = data.menuSpecificPrices;
    if (!legacy || typeof legacy !== "object" || Array.isArray(legacy)) {
      return [];
    }
    const refs: MenuGroupReference[] = [];
    for (const [key, value] of Object.entries(
      legacy as Record<string, unknown>,
    )) {
      const menuGroupId = parseInt(key, 10);
      const price = typeof value === "number" ? value : parseFloat(String(value));
      if (!isNaN(menuGroupId) && menuGroupId > 0 && !isNaN(price) && price >= 0) {
        refs.push(new MenuGroupReference({ menuGroupId, price }));
      }
    }
    return refs;
  }

  // ============================================================================
  // VARIANT HELPERS
  // ============================================================================

  /**
   * Find a variant by matching option values
   * @param options - Object with dimension names as keys and values to match
   * @returns The matching variant or undefined
   */
  findVariant(options: Record<string, string>): ItemVariant | undefined {
    return this.variants.find((v) => v.matchesOptions(options));
  }

  /**
   * Find a variant by SKU, UPC, or barcode
   * @param code - The code to search for
   * @returns The matching variant or undefined
   */
  findVariantByCode(code: string): ItemVariant | undefined {
    return this.variants.find((v) => v.matchesCode(code));
  }

  /**
   * Get all unique values for a specific variant option (dimension)
   * @param optionName - The dimension name (e.g., "Flavor", "Size")
   * @returns Array of unique values sorted alphabetically
   */
  getOptionValues(optionName: string): string[] {
    const values = new Set<string>();
    this.variants.forEach((v) => {
      const value = v.getOption(optionName);
      if (value) values.add(value);
    });
    return Array.from(values).sort();
  }

  // ============================================================================
  // AVAILABILITY HELPERS
  // ============================================================================

  /**
   * Check if item is currently available for ordering
   */
  isAvailable(): boolean {
    // Check if archived or inactive
    if (this.isArchived || this.isActive === false) {
      return false;
    }

    // The 86 question itself is `isInStock` — ONE implementation, shared with
    // server-side checkout, the storefront and `Modifier.isAvailable()`. This
    // method used to carry its own copy, and the copy is how the two came to
    // disagree: checkout read `isSuspended`/`stockInfo` while both operator
    // surfaces write `alwaysAvailable`/`quantityRemaining`, so a hand-86'd item
    // was refused by the POS and charged online (House of Pho, 2026-09-19).
    //
    // One deliberate correction came with the move: a raised `isSuspended` with
    // NO `suspendedUntil` now reads as 86'd. The old copy fell through to
    // "available" for it, which is what an INDEFINITE suspension always is —
    // the shape `stockManagementService` writes when it auto-suspends. In
    // practice it also writes `stockInfo.status`, so this changes no live
    // answer; it stops the two from disagreeing if one ever arrives alone.
    return isInStock(this);
  }

  /**
   * Check if item is available on a specific channel
   */
  isAvailableOnChannel(channel: SalesChannel): boolean {
    if (!this.isAvailable()) return false;

    // Check granular channel control
    if (this.disabledChannels?.includes(channel)) return false;
    if (this.enabledChannels && !this.enabledChannels.includes(channel))
      return false;

    // Check legacy flags
    switch (channel) {
      case "POS":
        return this.pos;
      case "KIOSK":
        return this.kiosk;
      case "ONLINE_ORDERING":
        return this.onlineOrdering;
      case "CUSTOMER_INVOICE":
        return this.customerInvoices;
      case "UBER_EATS":
      case "DOORDASH":
      case "GRUBHUB":
      case "GOOGLE":
      case "YELP":
        return this.onlineOrderingPartners;
      default:
        return true;
    }
  }

  /**
   * Check if item is available at a specific time
   */
  isAvailableAtTime(dayOfWeek: string, time: string): boolean {
    if (!this.availabilityWindows || this.availabilityWindows.length === 0) {
      return true; // No restrictions = always available
    }

    return this.availabilityWindows.some((window) => {
      if (window.dayOfWeek !== dayOfWeek.toUpperCase()) return false;
      return time >= window.startTime && time <= window.endTime;
    });
  }

  // ============================================================================
  // PRICING HELPERS
  // ============================================================================

  /**
   * Get the effective price for a channel (with markup if applicable)
   */
  getPriceForChannel(channel: SalesChannel): number | undefined {
    // Check for channel-specific override
    const override = this.channelPrices?.find((cp) => cp.channel === channel);
    if (override) {
      if (override.price !== undefined) return override.price;
      if (override.markupPercent !== undefined && this.basePrice) {
        return Math.round(this.basePrice * (1 + override.markupPercent / 100));
      }
      if (override.markupAmount !== undefined && this.basePrice) {
        return this.basePrice + override.markupAmount;
      }
    }

    // Check for general delivery markup
    if (this.deliveryMarkupPercent && this.basePrice) {
      const isDeliveryChannel = [
        "UBER_EATS",
        "DOORDASH",
        "GRUBHUB",
        "DELIVERY",
      ].includes(channel);
      if (isDeliveryChannel) {
        return Math.round(
          this.basePrice * (1 + this.deliveryMarkupPercent / 100),
        );
      }
    }

    return this.basePrice;
  }

  // ============================================================================
  // PLATFORM SYNC HELPERS
  // ============================================================================

  /**
   * Get external ID for a specific platform
   */
  getExternalId(platform: string): string | undefined {
    return this.externalIds?.find((e) => e.platform === platform)?.externalId;
  }

  /**
   * Check if item needs sync to a platform
   */
  needsSync(platform: string): boolean {
    const external = this.externalIds?.find((e) => e.platform === platform);
    if (!external) return true; // Never synced
    if (external.syncStatus === "PENDING" || external.syncStatus === "ERROR")
      return true;
    if (
      external.lastSynced &&
      this.updatedAt &&
      external.lastSynced < this.updatedAt
    )
      return true;
    return false;
  }

  // ============================================================================
  // COMPLIANCE HELPERS
  // ============================================================================

  /**
   * Check if item has age restriction
   */
  hasAgeRestriction(): boolean {
    if (this.ageRestriction && this.ageRestriction !== "NONE") return true;
    if (this.containsAlcohol || this.alcoholInfo?.containsAlcohol) return true;
    if (this.containsTobacco || this.containsCannabis) return true;
    return false;
  }

  /**
   * Get minimum age required to purchase
   */
  getMinimumAge(): number {
    if (this.ageRestriction === "AGE_25") return 25;
    if (this.ageRestriction === "AGE_21" || this.containsAlcohol) return 21;
    if (this.ageRestriction === "AGE_19") return 19;
    if (this.ageRestriction === "AGE_18" || this.containsTobacco) return 18;
    return 0;
  }

  /**
   * Get all allergen warnings
   */
  getAllergenWarnings(): string[] {
    const warnings: string[] = [];
    if (this.allergens) {
      warnings.push(...this.allergens);
    }
    return warnings;
  }

  /**
   * Check if item matches dietary requirement
   */
  matchesDietaryRequirement(label: DietaryLabel): boolean {
    return this.dietaryLabels?.includes(label) ?? false;
  }
}
